import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {HomepageV4Query} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {PageUID} from '@/utils/link-resolver'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import locales from '@helios/shared/locale/locales'
import {useLocale} from '@helios/shared/providers/locale'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

import {SliceZone} from './slice-zone'

type Props = PageProps<HomepageV4Query>

export const Homepage = (props: Props) => {
    const data = props.data.prismicHomepageV4.data
    const currentLocale = useLocale()

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header
                hideLeftNav={currentLocale.isPreLaunch || data.hide_left_nav}
                hideRightNav={currentLocale.isPreLaunch || data.hide_right_nav}
            />

            <Main>
                <SliceZone slices={data.body} trackVisibleSections={data.track_visible_sections} />
            </Main>

            <Footer hideLinks={data.hide_links} />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<HomepageV4Query>) => {
    const pageData = data.prismicHomepageV4.data

    // Can't access React Context here (until gatsby@5.6.0), so useLocale() doesn't work
    const locale = locales.find((locale) => {
        return locale.code === pageContext.locale
    })

    const canonicalUrl =
        pageContext.uid === PageUID.HOMEPAGE
            ? `/${locale?.urlCode}`
            : `/${locale?.urlCode}/${PageUID.LP}/${pageContext.uid}`

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            preventIndexing={pageData.exclude_robots}
            canonicalLocation={canonicalUrl}
        />
    )
}

export const query = graphql`
    query HomepageV4(
        $locale: String!
        $uid: String!
        $imageBreakpoints: [Int]
        $threeColumns: String
        $twoColumnsAsymmetric: String
    ) {
        prismicHomepageV4(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                exclude_robots
                hide_left_nav
                hide_right_nav
                hide_links
                track_visible_sections
                body {
                    ...FeatureBlockWithMarquee
                    ...HomepageV4CustomerLogosPlusCta
                    ...VideoHeroV4
                    ...GetStartedV4
                    ...StickyNavSections
                    ...ResourceSlider
                    ...AnimationBlock
                    ...FeaturePanel
                    ...UseCaseV4
                }
            }
        }
    }

    fragment FeatureBlockWithMarquee on PrismicHomepageV4DataBodyFeatureBlockWithMarquee {
        __typename
        primary {
            title_over_section
            title
            inverse
            headline
            body1 {
                richText
            }
            pre_cta_links {
                richText
            }
            cta_label
            cta_link {
                ...PrismicLink
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
        items {
            logo {
                alt
                url
                copyright
                dimensions {
                    height
                    width
                }
            }
            link {
                ...PrismicLink
            }
        }
    }

    fragment HomepageV4CustomerLogosPlusCta on PrismicHomepageV4DataBodyCustomerLogosPlusCta {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            marquee_mode
            background_color
            stat_1_highlight
            stat_2_highlight
            stat_3_highlight
            stat_1_body
            stat_2_body
            stat_3_body
        }
        items {
            logo {
                alt
                url
                copyright
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment VideoHeroV4 on PrismicHomepageV4DataBodyVideoHero {
        __typename
        primary {
            title
            lead_text {
                richText
            }
            signup_label
            cta_text
            cta_link {
                ...PrismicLink
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            video_poster_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment GetStartedV4 on PrismicHomepageV4DataBodyGetStarted {
        __typename
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment StickyNavSections on PrismicHomepageV4DataBodyStickyNavSections {
        __typename
        primary {
            navigation_text
            cta_text
        }
        items {
            title
            subtitle {
                richText
            }
            image {
                alt
                url
                copyright
                dimensions {
                    height
                    width
                }
                gatsbyImageData(layout: FULL_WIDTH)
            }
            card_1_title
            card_1_body
            card_1_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            card_1_video_poster {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            card_1_video_webm {
                ...Video
            }
            card_1_video_mp4 {
                ...Video
            }
            card_2_title
            card_2_body
            card_2_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            card_2_video_poster {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            card_2_video_webm {
                ...Video
            }
            card_2_video_mp4 {
                ...Video
            }
            card_3_title
            card_3_body
            card_3_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            card_3_video_poster {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            card_3_video_webm {
                ...Video
            }
            card_3_video_mp4 {
                ...Video
            }
        }
    }

    fragment ResourceSlider on PrismicHomepageV4DataBodyResourceSlider {
        __typename
        primary {
            title
            description
            top_link_text
            top_link {
                ...PrismicLink
            }
        }
        items {
            card_title
            card_description
            card_cta
            to {
                ...PrismicLink
            }
            card_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
            }
            category
            language
        }
    }

    fragment AnimationBlock on PrismicHomepageV4DataBodyAnimationBlock {
        __typename
        primary {
            title
            body: description {
                richText
            }
            lottie_json_file {
                url
            }
            animation_frames
            placeholder {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
        }
    }

    fragment FeaturePanel on PrismicHomepageV4DataBodyFeaturePanel {
        __typename
        primary {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            background_color
            is_media_on_the_right
            subtitle
            subtitle_color
            title
            content {
                richText
            }
            cta_label
            cta_link {
                ...PrismicLink
            }
        }
    }

    fragment UseCaseV4 on PrismicHomepageV4DataBodyUseCase {
        __typename
        primary {
            title
            subtitle
            cta
            secondary_cta_label
            secondary_cta_link {
                ...PrismicLink
            }
        }
        items {
            label
            value
            icon {
                fluid {
                    base64
                }
            }
        }
    }
`

export default withPrismicPreview(Homepage)
